import React from 'react';
import {
    Button,
    Container,
    Paper,
    TextField,
    FormControl,
    MenuItem,
    Select,
    InputLabel,
    OutlinedInput,
    IconButton,
    InputAdornment,
    Chip,
    Tooltip,
    useTheme,
    Popover,
    Badge
} from '@material-ui/core';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import FilterListIcon from '@material-ui/icons/FilterList';
import CloseIcon from '@material-ui/icons/Close';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import axios from 'axios';
import {nanoid} from 'nanoid'
import copy from 'copy-to-clipboard';
import {
    useLocation
} from "react-router-dom";
// import MapBackground from './BLANK_MAP_2021.svg'
import Table from './components/table'
import FilterFields from './components/filterFields'

export type RecordType = 'A' | 'AAAA' | 'CNAME' | 'TXT' | 'MX' | 'NS' | 'SOA' | 'SRV' | '';

interface SonarAgentObj {
    id: number;
    name: string;
    label: string;
    location: string;
    country: string;
    region: string;
}

export interface LookupParams {
    nameServer: string;
    location: string;
    host: string;
    type: RecordType;
}

export interface DigResult {
    answer: string;
    ttl: number;
}

export interface MxResult {
    priority: number;
    value: string;
    ttl: number
}

export interface SoaResult {
    serial: number;
    expire: number;
    refresh: number;
    retry: number;
}

export interface SrvResult {
    port: number
    priority: number
    weight: number
    target: string;
    ttl: number;
}

interface LookupResponse {
    checkedat: string;
    host: string;
    location: string;
    recordtype: string;
    responsetime: number;
    result: Array<DigResult | MxResult | SoaResult | SrvResult>;
}

export interface ExtendedLookupResponse extends LookupResponse {
    nameServer: string;
}

export interface AgentList {
    location: string;
    name: string;
    host: string;
}


// This method grabs the URL
function useQuery() {
    return new URLSearchParams(useLocation().search);
}


/**
 * Makes an API call which returns the results of a DNS Lookup based on the provided params
 * @param queryParams
 * @return Promise for an object containing the results of the DNS Lookup
 */
function dnsLookup(queryParams: LookupParams): Promise<LookupResponse> {
    return axios({
        method: 'post',
        url: 'https://dnstb-backend.herokuapp.com/dig',
        //this is for testing backend locally
        // url: 'http://localhost:80/dig',
        data: queryParams
    }).then(response => response.data)
}

function findStoredUrlData(id: string) {
    return axios({
            method: 'post',
            url: "https://dnstb-backend.herokuapp.com/shortener/find",
            data: {"id": id}
        }
    ).then(response => response.data)
}

// This method grabs the sonar agent locations from the sonar API
function getSonarAgents() {
    return axios.get('https://dnstb-backend.herokuapp.com/agents')
}

// Primary application
export default function App() {
    const baseUrl = "https://constellix.com/dns-tools/dns-lookup"
    // const baseUrl = "http://localhost:3001"
    const theme = useTheme()
    const inputLabel = React.useRef(null);
    const urlParamId = useQuery().get('id')
    const [currentUrl, setCurrentUrl] = React.useState(baseUrl)
    const [checkRan, setCheckRan] = React.useState(false) // this is used for the URL Shortener only
    const [domainMatch, setDomainMatch] = React.useState(true)
    const [resolverMatch, setResolverMatch] = React.useState(true)
    const [labelWidth, setLabelWidth] = React.useState(0);
    const [agents, setAgents] = React.useState<Array<AgentList>>([]);
    const [queryParamHistory, setQueryParamHistory] = React.useState<Array<LookupParams>>([])
    // const [filteredLookups, setFilteredLookups] = React.useState<Array<ExtendedLookupResponse>>([])
    const [queryParams, setQueryParams] = React.useState<LookupParams>(
        {
            nameServer: "8.8.8.8",
            location: "uswas01-mon01.nodes.constellix.net",
            host: "",
            type: "A",
        }
    )
    // const [filtered, setFiltered] = React.useState<Array<ExtendedLookupResponse>>([]);
    const [digResults, setDigResults] = React.useState<Array<ExtendedLookupResponse>>([]);
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [appliedFilter, setAppliedFilter] = React.useState<LookupParams>()
    const domainRegex = /([a-z0-9|-]+\.)*[a-z0-9|-]+\.[a-z]+/
    const ipRegex = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/
    const ipvSixRegex = /(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))/

    React.useEffect(() => {
        // @ts-ignore
        setLabelWidth(inputLabel.current.offsetWidth);

        /**
         * If url param "id" is provided this will make a call with the ID to retrieve lookup data from MongoDb
         * then it will map through the query data and run the lookups.
         */
        if (urlParamId) {
            if (!urlParamId) {
                return
            }

            setCurrentUrl(`${baseUrl}/?id=${urlParamId}`)
            findStoredUrlData(urlParamId).then(response => {
                const convertedData = response[0].values.map((obj: any) => {
                    obj['type'] = obj['recordType']
                    delete obj['recordType']
                    delete obj['_id']
                    return obj
                })

                let temp: any = []
                convertedData.map((obj: LookupParams) => {
                    dnsLookup(obj).then(response => {
                        if (!response) return
                        const enrichedResponse: ExtendedLookupResponse = {
                            ...response,
                            nameServer: queryParams.nameServer
                        }
                        temp = [...temp, enrichedResponse]
                        setDigResults(temp)
                    }).catch(error => console.log("Error:", error))
                })

                setQueryParamHistory(convertedData)
            })
        }

        /**
         * This function makes a call to the backend to grab all Sonar Agents.
         * Then it enriches the data so that it can be use in future DNS request and saves that data to an Arry in state.
         */
        getSonarAgents().then(response => {

            let agentArr: Array<AgentList> = [];

            response.data.map((agentObj: SonarAgentObj, index: number) => {

                // converts agentArr to a simple array with location names
                const tempAgenArr = () => agentArr.map((agentList: AgentList) => {
                    return agentList.location
                })

                if (!tempAgenArr().find((location: string) => location === agentObj.location)) {
                    const agentUrl = `${agentObj.name.toLocaleLowerCase().slice(0, 5)}01-mon01.nodes.constellix.net`
                    agentArr = [...agentArr, {location: agentObj.location, name: agentObj.name, host: agentUrl}]
                }
            })
            setAgents(agentArr)
        })
    }, [])

    function storeUrlData(urlData: Array<LookupParams>) {
        if (checkRan) {
            const enrichedData = urlData.map((obj: any) => {
                obj['recordType'] = obj['type']
                delete obj['type']
                return obj
            })
            const objectId = nanoid()

            const dataToSend = {
                id: objectId,
                values: enrichedData
            }

            axios({
                    method: 'post',
                    url: "https://dnstb-backend.herokuapp.com/shortener",
                    data: dataToSend
                }
            ).catch(error => console.log("Error:", error))
            setCurrentUrl(`${baseUrl}/?id=${objectId}`)
            copy(`${baseUrl}/?id=${objectId}`)
            setCheckRan(false)
        } else {
            copy(currentUrl)
        }
    }

    function deleteRow(row: string) {
        const rowIndex = digResults.map(function (item) {
            return item.checkedat;
        }).indexOf(row);
        const tempDigResults = [...digResults]
        const tempParamHistory = queryParamHistory
        tempParamHistory.splice(rowIndex, 1)
        tempDigResults.splice(rowIndex, 1)
        setDigResults(tempDigResults);
    }

    // Clears all results
    function clearAll() {
        setCurrentUrl(baseUrl)
        setDigResults([])
        setAppliedFilter(undefined)
        setQueryParamHistory([])
    }

    // Updates QueryParams state when a domain name is entered
    function handleDomainChange(name: string) {
        setQueryParams({...queryParams, host: name})
    }

    // Updates QueryParams state when a resolver is entered
    function handleResolverChange(resolver: string) {
        setQueryParams({...queryParams, nameServer: resolver})
    }

    // Updated when the user selects a different record type this updates the record type in state
    function handleRecordTypeChange(val: RecordType) {
        setQueryParams({...queryParams, type: val})
    }

    function handleLocationChange(val: string) {
        setQueryParams({...queryParams, location: val})
    }

    // Checks to see if the Domain and Resolver fileds are filled out
    function checkQueryFields() {
        let domain = true
        let resolver = true
        let resolverArr = [!queryParams.nameServer.match(domainRegex), !queryParams.nameServer.match(ipRegex), !queryParams.nameServer.match(ipvSixRegex)]

        if (!queryParams.host.match(domainRegex)) {
            domain = false
            setDomainMatch(false)
        } else if (queryParams.host.length > 2 && !domainMatch) {
            setDomainMatch(true)
        }
        if (resolverArr.includes(false)) {
            if (!resolverMatch) {
                setResolverMatch(true)
            }
        } else {
            resolver = false;
            setResolverMatch(false)
        }


        if (!domain || !resolver) {
            return false
        } else {
            return true
        }
    }


    // Exicutes the DNS Lookup when the "Run" button is clicked
    function handelClick() {
        if (!checkQueryFields()) {
            return
        }
        const tempQueryParamHistory = [...queryParamHistory, queryParams]
        dnsLookup(queryParams)
            .then((response) => {
                if (typeof response === typeof "") {
                    console.error(`Error: ${response}`)
                    return
                }
                if (!response) {

                    return
                }
                const enrichedResponse: ExtendedLookupResponse = {...response, nameServer: queryParams.nameServer}
                setDigResults([enrichedResponse, ...digResults])
                setQueryParamHistory(tempQueryParamHistory)
                setCheckRan(true)
            })
            .catch(error => console.log(error))
    }

    function openFilter(event: React.MouseEvent<HTMLButtonElement>) {
        setAnchorEl(event.currentTarget);
    }

    function handleClosePopover() {
        setAnchorEl(null);
    }

    function applyFilter(filterFields: LookupParams | undefined): void {
        setAppliedFilter(filterFields)
    }

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    return (
        <div className="App">
            <Container maxWidth='xl' style={{background: `${theme.palette.secondary.main}`, height: '1000px'}}>
                <div style={{display: 'flex', width: '100%', marginBottom: '10px'}}>
                    <TextField
                        error={domainMatch ? false : true}
                        style={{marginRight: '10px'}}
                        value={queryParams.host}
                        onChange={(event) => {
                            handleDomainChange(event.target.value)
                        }}
                        id="outlined-basic"
                        label="Domain Name"
                        variant="outlined"
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    <FormControl variant="outlined">
                        <InputLabel
                            ref={inputLabel}
                            id="demo-simple-select-outlined-label"
                            shrink
                        >Record Type</InputLabel>
                        <Select
                            style={{marginRight: '10px', width: '110px'}}
                            labelId="demo-simple-select-outlined-label"
                            id="demo-simple-select-outlined"
                            value={queryParams.type}
                            onChange={(event: any) => {
                                handleRecordTypeChange(event.target.value)
                            }}
                            label="Record Type"
                            input={
                                <OutlinedInput
                                    notched
                                    labelWidth={95}
                                    name="Record Type"
                                    id="outlined-age-always-notched"
                                />
                            }
                        >
                            <MenuItem value='A'>A</MenuItem>
                            <MenuItem value='AAAA'>AAAA</MenuItem>
                            <MenuItem value='CNAME'>CNAME</MenuItem>
                            <MenuItem value='MX'>MX</MenuItem>
                            <MenuItem value='TXT'>TXT</MenuItem>
                            <MenuItem value='NS'>NS</MenuItem>
                            <MenuItem value='SOA'>SOA</MenuItem>
                            <MenuItem value='SRV'>SRV</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined">
                        <InputLabel
                            ref={inputLabel}
                            id="demo-simple-select-outlined-label"
                            shrink
                        >Location</InputLabel>
                        <Select
                            style={{marginRight: '0', width: '227px'}}
                            labelId="demo-simple-select-outlined-label"
                            id="demo-simple-select-outlined"
                            value={queryParams.location}
                            onChange={(event: any) => handleLocationChange(event.target.value)}
                            label="Age"
                            input={
                                <OutlinedInput
                                    notched
                                    labelWidth={labelWidth}
                                    name="Location"
                                    id="outlined-age-always-notched"
                                />
                            }
                        >
                            {agents.map((agent: any) => {
                                return <MenuItem value={agent.host}>{agent.location}</MenuItem>
                            })}
                        </Select>
                    </FormControl>
                    <TextField
                        error={resolverMatch ? false : true}
                        style={{marginLeft: '10px'}}
                        value={queryParams.nameServer}
                        onChange={(event) => {
                            handleResolverChange(event.target.value)
                        }}
                        id="outlined-basic"
                        label="Resolver"
                        variant="outlined"
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    <Button
                        style={{marginLeft: '10px'}}
                        size='small'
                        variant="contained"
                        color="primary"
                        endIcon={<PlayArrowIcon/>}
                        onClick={handelClick}
                    >
                        Run
                    </Button>
                </div>
                <Paper
                    style={{
                        height: '100vh',
                        width: '90vw',
                        borderRadius: '0 20px 0 20px',
                        padding: '5px',
                        backgroundColor: `${theme.palette.primary.main}`,
                        overflow: 'hidden',
                        border: `5px solid ${theme.palette.primary.main}`
                    }}
                >
                    <div style={{display: 'flex', justifyContent: 'space-between', padding: '0 10px'}}>
                        <div>
                            <Badge invisible={!appliedFilter} color="error" variant="dot">
                            <Button onClick={openFilter} color='primary'
                                    style={{backgroundColor: `${theme.palette.secondary.main}`}}
                                    endIcon={<FilterListIcon/>}>
                                Filters
                            </Button>
                            </Badge>
                            <Popover
                                id={id}
                                open={open}
                                anchorEl={anchorEl}
                                onClose={handleClosePopover}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                }}
                            >
                                <FilterFields queryParams={queryParamHistory} applyFilter={applyFilter}
                                              currentFilter={appliedFilter} handleClosePopover={handleClosePopover}/>
                            </Popover>
                            <Chip style={{fontWeight: 'bold', marginLeft: '30px'}} color='secondary'
                                  label={"Checks: " + digResults.length}/>
                        </div>
                        <div>
                            <Tooltip title='Click to copy link'>
                                <Chip onClick={() => storeUrlData(queryParamHistory)} color='secondary'
                                      icon={<InsertDriveFileIcon style={{color: `${theme.palette.primary.main}`}}/>} label="Copy URL Link" clickable/>
                            </Tooltip>
                            <Button onClick={() => clearAll()} color='primary'
                                    style={{backgroundColor: `${theme.palette.secondary.main}`, marginLeft: '40px'}}
                                    endIcon={<CloseIcon/>}>
                                Clear All
                            </Button>
                        </div>
                    </div>
                    <Table deleteRow={(row: string) => deleteRow(row)} lookups={digResults}
                           appliedFilter={appliedFilter}/>
                </Paper>
            </Container>
        </div>
    );
}

// TODO: Need to ask Anteneh if he can return the TTL of the check
// TODO: Make it so that if they click the copy url link once, it will only make DB call if they add another check after that